import { AnimatedTicketBg } from "@assets";
import { Image } from "@chakra-ui/react";
import { unique } from "@lib/utils";
import { NFTEvent, NFTEventInfo } from "@models/event";
import { MS_END } from "@models/timezone";
import { useMemo, useRef, useState } from "react";

const _t = (key?: string) => key || "";

const AnimatedTicket = ({
  imageUrl,
  info,
  event,
  itemsCount,
}: {
  imageUrl: string;
  event: NFTEvent;
  info: NFTEventInfo;
  itemsCount: { [key: string]: number };
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardStyle, setCardStyle] = useState({});

  const cardHeight = useMemo(() => {
    try {
      if (!cardRef.current) return 0;
      const computedStyle = getComputedStyle(cardRef.current);
      if (!computedStyle?.height) return 0;
      return parseFloat(computedStyle.height.replace("px", ""));
    } catch (error) {
      return 0;
    }
  }, [cardRef.current]);

  const cardWidth = useMemo(() => {
    try {
      if (!cardRef.current) return 0;
      const computedStyle = getComputedStyle(cardRef.current);
      if (!computedStyle?.width) return 0;
      return parseFloat(computedStyle.width.replace("px", ""));
    } catch (error) {
      return 0;
    }
  }, [cardRef.current]);

  const movementOffset = 50;

  const onMouseMove = (e) => {
    e.preventDefault();
    if (!cardRef.current || !cardHeight || !cardWidth) return;
    const card = cardRef.current;
    const pos =
      e.type === "touchmove"
        ? [e.nativeEvent.touches[0].clientX, e.nativeEvent.touches[0].clientY]
        : [e.nativeEvent.offsetX, e.nativeEvent.clientY];

    // math for mouse position
    const [l, t] = pos;
    // console.log(cardHeight, cardWidth, l, t);
    var px = Math.abs(Math.floor((100 / cardWidth) * l) - 100);
    var py = Math.abs(Math.floor((100 / cardHeight) * t) - 100);
    var pa = movementOffset - px + (movementOffset - py);

    // math for gradient / background positions
    var lp = movementOffset + (px - movementOffset) / 1.5;
    var tp = movementOffset + (py - movementOffset) / 1.5;

    var ty = Math.round(((tp - movementOffset) / 2) * -1);
    var tx = Math.round(((lp - movementOffset) / 1.5) * 0.5);

    card.classList.remove("active");
    card.classList.remove("animated");
    setCardStyle({
      transform: `rotateX(${ty}deg) rotateY(${tx}deg)`,
    });
  };

  const onMouseOut = (e) => {
    setCardStyle({});
  };

  const datetime =
    info.date || info.time
      ? unique([info.date, info.time]).join(", ")
      : event.duration;

  return (
    <div className="animated-ticket">
      <div
        className="card animated"
        ref={cardRef}
        onMouseMove={onMouseMove}
        onTouchMove={onMouseMove}
        onMouseOut={onMouseOut}
        onTouchEnd={onMouseOut}
        onTouchCancel={onMouseOut}
        style={{
          backgroundImage: `url('${AnimatedTicketBg.src}')`,
          ...cardStyle,
        }}
      >
        <div className="flex flex-col text-white">
          {/* Header */}
          <div className="flex flex-row p-4 items-center border-b">
            {info.organizer.logo && (
              <Image
                flexShrink={0}
                boxSize={8}
                objectFit="contain"
                src={info.organizer.logo}
                alt={info.organizer.name}
              />
            )}
            <div className="flex flex-col flex-grow justify-center items-end">
              <p className="uppercase text-xs text-primary">
                {info.organizer && info.organizer.name
                  ? info.organizer.name
                  : ""}
              </p>
              <p className="font-medium">{info.name}</p>
            </div>
          </div>
          {/* Ticket Image */}
          <Image boxSize="280px" objectFit="cover" src={imageUrl} alt="" />
          {/* Ticket Details */}
          <div className="grid grid-cols-1 gap-4 p-4 border-t flex-grow">
            <div className="grid grid-cols-1 gap-1">
              <h2 className="uppercase text-xs text-primary">TICKET</h2>
              <p className="font-medium">
                {Object.keys(itemsCount)
                  .map((name) => `${itemsCount[name]}  x  ${name}`)
                  .join(", ")}
              </p>
            </div>

            {event.start > 0 && event.end !== MS_END && (
              <div className="grid grid-cols-1 gap-1">
                <h2 className="uppercase text-xs text-primary">DATE</h2>
                <p className="font-medium">{datetime}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default AnimatedTicket;
